import { translator } from "../../translation/translator";
import { UserModalApi } from "../modal/user-modal-api";
import { PointHistoryRender } from "../point-history/point-history-render";
import { PointHistoryApi, type PointHistoryItem } from "../point-history/point-history-api";
import { UserProfileTabsRender } from "./user-profile-tabs-render";
import { UserFollowSetup } from "../follow/user-follow-setup";
import { UserFollowRender } from "../follow/user-follow-render";
import { Roles } from "../../constants/roles";
import { toPositiveIntOrNull } from "../../util/id-utils";
import { PointHistoryAdminSetup } from "../point-history-admin/point-history-admin-setup";

interface UserProfileTabsFormLike {
    getUserId(): number;
    getPointHistoryPanel(): HTMLElement | null;
    getPointHistoryIncludeInvalidInput(): HTMLInputElement | null;
    getHashTab(): string;
    hasTab(tab: string): boolean;
    getDefaultTab(): string;
    getFirstTab(): string;
    getLayoutRoot(): HTMLElement | null;
    getTabsRoot(): HTMLElement | null;
    getTabButtons(): HTMLElement[];
    getStatTriggers(): HTMLElement[];
    getTabList(tab: string): HTMLElement | null;
    getPanelsRoot(): HTMLElement;
    getPointHistoryTotal(): HTMLElement | null;
    getTabCountElements(tab: string): HTMLElement[];
    hasSsrTabContent(root: ParentNode, tab: string): boolean;
    getFavoriteCard(root: EventTarget | Element | null | undefined): HTMLElement | null;
    getProductLikeButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getFollowingCard(root: EventTarget | Element | null | undefined): HTMLElement | null;
    getTabRow(root: EventTarget | Element | null | undefined): HTMLElement | null;
    getFavoritesList(root: EventTarget | Element | null | undefined): HTMLElement | null;
    getFollowingsList(root: EventTarget | Element | null | undefined): HTMLElement | null;
    hasTabRows(root: ParentNode): boolean;
    hasFollowingCards(root: ParentNode): boolean;
}

interface PointHistoryAdminInitResult {
    panel?: HTMLElement;
    form?: {
        isInitialized?: () => boolean;
    };
    destroy?: () => void;
}

type TabName = "missions" | "point-history" | "posts" | "followers" | "followings" | "favorites";

const LAZY_LOAD_TABS: TabName[] = ["missions", "point-history", "posts", "followers", "favorites"];

export class UserProfileTabsHandler {
    public form: UserProfileTabsFormLike;

    public userId: number;

    public loadedTabs: Set<string>;

    public pointHistoryRender: PointHistoryRender;

    public pointHistoryAll: PointHistoryItem[];

    public pointHistoryIncludeInvalid: boolean;

    public canManagePoints: boolean;

    public pointHistoryAdminSetup: PointHistoryAdminInitResult | null;

    public constructor(form: UserProfileTabsFormLike) {
        this.form = form;
        this.userId = form.getUserId();
        this.loadedTabs = new Set();
        this.pointHistoryRender = new PointHistoryRender();
        this.pointHistoryAll = [];
        this.pointHistoryIncludeInvalid = false;
        this.canManagePoints = false;
        this.pointHistoryAdminSetup = null;
    }

    public bootstrap(): void {
        const pointHistoryPanel = this.form.getPointHistoryPanel();
        this.canManagePoints =
            pointHistoryPanel instanceof HTMLElement && pointHistoryPanel.dataset.canManagePoints === "1";
        const includeInvalidEl = this.form.getPointHistoryIncludeInvalidInput();
        this.pointHistoryIncludeInvalid =
            includeInvalidEl instanceof HTMLInputElement ? includeInvalidEl.checked : false;
        const hashTab = this.form.getHashTab();
        const initialTab = this.form.hasTab(hashTab) ? hashTab : this.form.getDefaultTab() || this.form.getFirstTab();
        this.activate(initialTab, { syncHash: true });
    }

    public activate(tab: string, options: { syncHash?: boolean } = {}): void {
        const { syncHash = true } = options;
        const layoutRoot = this.form.getLayoutRoot();
        const tabsRoot = this.form.getTabsRoot();
        if (!(layoutRoot instanceof HTMLElement)) return;
        if (!(tabsRoot instanceof HTMLElement)) return;
        if (!this.form.hasTab(tab)) return;

        const currentTab = tabsRoot.getAttribute("data-active-tab") || "";
        if (currentTab === tab) {
            UserProfileTabsRender.applyActiveTab(
                layoutRoot,
                tabsRoot,
                this.form.getTabButtons(),
                this.form.getStatTriggers(),
                tab,
            );
            if (syncHash) this.syncHash(tab);
            if (this.isLazyLoadTab(tab)) {
                void this.loadTabIfNeeded(tab);
            }
            return;
        }

        UserProfileTabsRender.applyActiveTab(
            layoutRoot,
            tabsRoot,
            this.form.getTabButtons(),
            this.form.getStatTriggers(),
            tab,
        );

        if (syncHash) {
            this.syncHash(tab);
        }

        if (this.isLazyLoadTab(tab)) {
            void this.loadTabIfNeeded(tab);
        }
    }

    public syncHash(tab: string): void {
        if (typeof window === "undefined") return;
        const nextHash = `#${tab}`;
        if (window.location.hash === nextHash) return;
        window.location.hash = tab;
    }

    public async loadTabIfNeeded(tab: string): Promise<void> {
        const list = this.form.getTabList(tab);
        if (!(list instanceof HTMLElement)) return;

        if (this.loadedTabs.has(tab)) return;

        if (tab === "posts") {
            if (list.dataset.ssrLoaded === "1") {
                this.loadedTabs.add(tab);
                return;
            }
        }

        if (tab === "favorites" || tab === "followers" || tab === "followings") {
            const hasActualSsrContent = this.form.hasSsrTabContent(list, tab);
            const hasSsrContent = list.dataset.ssrLoaded === "1" && hasActualSsrContent;
            if (hasSsrContent) {
                this.loadedTabs.add(tab);
                return;
            }
        }

        this.loadedTabs.add(tab);
        UserProfileTabsRender.renderLoading(list);

        try {
            if (tab === "missions") {
                list.innerHTML = "";
            } else if (tab === "point-history") {
                await this.refreshPointHistory(list);
            } else if (tab === "posts") {
                const posts = await UserModalApi.getPosts(this.userId, {
                    perPage: 100,
                    page: 1,
                });
                const html = UserProfileTabsRender.renderRankingStyleProductRows(posts);
                UserProfileTabsRender.renderWithFallback(
                    list,
                    html,
                    '<p class="text-muted text-center" data-translate="no_posts">No posts</p>',
                );
            } else if (tab === "followers") {
                const users = await UserModalApi.getFollowers(this.userId, {
                    perPage: 100,
                    page: 1,
                });
                const html = UserProfileTabsRender.renderUserRows(users);
                UserProfileTabsRender.renderWithFallback(
                    list,
                    html,
                    `<p class="text-muted text-center" data-translate="no_followers" data-role="${Roles.USER_FOLLOW_FOLLOWERS_EMPTY}">No followers</p>`,
                );
            } else if (tab === "followings") {
                const users = await UserModalApi.getFollowings(this.userId, {
                    perPage: 100,
                    page: 1,
                });
                const html = UserProfileTabsRender.renderUserRows(users, { followingsMode: true });
                UserProfileTabsRender.renderWithFallback(
                    list,
                    html,
                    '<p class="text-muted text-center" data-translate="no_following">ยังไม่ได้ติดตามใคร</p>',
                );
            } else if (tab === "favorites") {
                const products = await UserModalApi.getFavorites(this.userId, {
                    perPage: 100,
                    page: 1,
                });
                const html = UserProfileTabsRender.renderRankingStyleProductRows(products, { favoritesMode: true });
                UserProfileTabsRender.renderWithFallback(
                    list,
                    html,
                    '<p class="text-muted text-center" data-translate="no_favorites">ยังไม่มีสินค้าโปรด</p>',
                );
            }
        } catch (_error) {
            UserProfileTabsRender.renderLoadError(list);
        } finally {
            translator.updateContent();
            UserFollowSetup.init();
        }
    }

    public applyPointHistoryFilters(listOverride: HTMLElement | null = null): void {
        const list = listOverride instanceof HTMLElement ? listOverride : this.form.getTabList("point-history");
        if (!(list instanceof HTMLElement)) return;

        const filtered = this.pointHistoryAll.filter((history) => {
            const points = Number(history?.points_earned ?? 0);
            const isInvalid = Number(history?.delete_flag ?? 0) === 1;

            if (this.pointHistoryIncludeInvalid) {
                return isInvalid;
            }

            return !isInvalid && points !== 0;
        });

        this.pointHistoryRender.renderPointHistory(list, filtered, {
            canManagePoints: this.canManagePoints,
        });
        this.updatePointHistoryTotal(filtered);
        translator.updateContent();
    }

    public handlePointHistoryIncludeInvalidChange(): void {
        const includeInvalidEl = this.form.getPointHistoryIncludeInvalidInput();
        this.pointHistoryIncludeInvalid =
            includeInvalidEl instanceof HTMLInputElement ? includeInvalidEl.checked : false;
        void this.reloadPointHistory();
    }

    public async ensurePointHistoryAdmin(): Promise<void> {
        const panel = this.form.getPointHistoryPanel();
        if (!(panel instanceof HTMLElement)) return;

        if (!this.canManagePoints) return;

        if (
            this.pointHistoryAdminSetup &&
            this.pointHistoryAdminSetup.panel === panel &&
            this.pointHistoryAdminSetup.panel.isConnected &&
            this.pointHistoryAdminSetup.form.isInitialized()
        ) {
            return;
        }

        const includeInvalidEl = this.form.getPointHistoryIncludeInvalidInput();

        this.pointHistoryAdminSetup = PointHistoryAdminSetup.init(panel, {
            userId: this.userId,
            onReload: async () => {
                if (includeInvalidEl instanceof HTMLInputElement) {
                    includeInvalidEl.checked = this.pointHistoryIncludeInvalid;
                }
                await this.reloadPointHistory();
            },
        });
    }

    public async refreshPointHistory(listOverride: HTMLElement | null = null): Promise<void> {
        const list = listOverride instanceof HTMLElement ? listOverride : this.form.getTabList("point-history");
        if (!(list instanceof HTMLElement)) return;

        const histories = await PointHistoryApi.fetchPointHistory(this.userId, {
            includeInvalid: this.pointHistoryIncludeInvalid,
        });
        this.pointHistoryAll = histories;
        this.pointHistoryRender.renderPointHistory(list, this.pointHistoryAll, {
            canManagePoints: this.canManagePoints,
        });
        this.applyPointHistoryFilters(list);
        void this.ensurePointHistoryAdmin();
    }

    public async reloadPointHistory(): Promise<void> {
        await this.refreshPointHistory();
    }

    public updatePointHistoryTotal(histories: PointHistoryItem[]): void {
        const totalEl = this.form.getPointHistoryTotal();
        if (!(totalEl instanceof HTMLElement)) return;

        const total = (Array.isArray(histories) ? histories : []).reduce((sum, history) => {
            const points = Number(history?.points_earned ?? 0);
            return sum + (Number.isFinite(points) ? points : 0);
        }, 0);

        const sign = total >= 0 ? "+" : "";
        totalEl.textContent = `${sign}${this.pointHistoryRender.formatPointValue(total)}`;
    }

    public decrementFavoritesCount(): void {
        const countEls = this.form.getTabCountElements("favorites");
        countEls.forEach((el) => {
            if (!(el instanceof HTMLElement)) return;
            const current = UserFollowRender.parseCountText(el.textContent || "0");
            const next = Number.isFinite(current) ? Math.max(0, current - 1) : 0;
            el.textContent = UserFollowRender.formatCountText(next, true);
        });
    }

    public decrementFollowingsCount(): void {
        const countEls = this.form.getTabCountElements("followings");
        countEls.forEach((el) => {
            if (!(el instanceof HTMLElement)) return;
            const current = UserFollowRender.parseCountText(el.textContent || "0");
            const next = Number.isFinite(current) ? Math.max(0, current - 1) : 0;
            el.textContent = UserFollowRender.formatCountText(next, true);
        });
    }

    public handleFavoritesRemoveClick(event: Event): void {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const likeBtn = this.form.getProductLikeButton(target);
        if (!(likeBtn instanceof HTMLButtonElement)) return;

        const list = this.form.getFavoritesList(likeBtn);
        if (!(list instanceof HTMLElement)) return;

        const likedClass = (likeBtn.dataset.likedClass || "").trim() || "products-liked";
        const isLiked =
            likeBtn.dataset.isLiked === "true" ||
            likeBtn.getAttribute("aria-pressed") === "true" ||
            likeBtn.classList.contains(likedClass);

        if (!isLiked) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const card = this.form.getFavoriteCard(likeBtn);
        if (!(card instanceof HTMLElement)) return;

        const productId = toPositiveIntOrNull(card.dataset.favoriteProductId ?? card.dataset.productId);
        const row = this.form.getTabRow(card);

        this.decrementFavoritesCount();

        if (row instanceof HTMLElement) {
            row.remove();
        } else {
            card.remove();
        }

        if (list instanceof HTMLElement && !this.form.hasTabRows(list)) {
            list.innerHTML = '<p class="text-muted text-center" data-translate="no_favorites">ยังไม่มีสินค้าโปรด</p>';
            translator.updateContent();
        }

        if (productId !== null) {
            void UserModalApi.removeFavorite(productId).catch(() => {
                return undefined;
            });
        }
    }

    private isLazyLoadTab(tab: string): tab is TabName {
        return LAZY_LOAD_TABS.includes(tab as TabName);
    }
}
